// Status / deployment-type / environment colors for the v2 UI.
//
// Mirrors the API of src/theme/tokens.ts but recolors to the Notion sticker
// palette (see v2/theme/tokens.ts). Status appears as small dots and quiet
// tag-style pills on white surfaces — never as tinted card backgrounds.

import { PaletteMode, sticker } from './tokens';

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export type CanonicalStatus =
  | 'success'
  | 'failed'
  | 'inprogress'
  | 'pending'
  | 'cancelled'
  | 'unknown';

export const normalizeStatus = (status?: string): CanonicalStatus => {
  switch (status?.toLowerCase()) {
    case 'success':
      return 'success';
    case 'failed':
      return 'failed';
    case 'inprogress':
    case 'in_progress':
    case 'in progress':
      return 'inprogress';
    case 'pending':
      return 'pending';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'unknown';
  }
};

export const statusHues: Record<CanonicalStatus, string> = {
  success: sticker.green, // #1aae39
  failed: '#d93a3a', // Notion has no structural red — warm red tuned to palette
  inprogress: '#0b76d1', // readable deployment blue (distinct from primary CTA)
  pending: sticker.orange, // #dd5b00
  cancelled: '#787774',
  unknown: '#9b9a97',
};

export const getStatusHue = (status?: string): string =>
  statusHues[normalizeStatus(status)];

export const statusLabels: Record<CanonicalStatus, string> = {
  success: 'Success',
  failed: 'Failed',
  inprogress: 'Deploying',
  pending: 'Pending',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
};

// ---------------------------------------------------------------------------
// Deployment type
// ---------------------------------------------------------------------------

export const typeHues: Record<string, string> = {
  lambda: '#2f80c2', // sky, deepened for legibility
  ecs: sticker.teal, // #2a9d99
  terraform: '#8155b8', // purple family (sticker purple deepened)
  static: sticker.green, // #1aae39
  adhoc: '#d63c93', // pink, deepened
  'agentcore-runtime': sticker.orange, // #dd5b00
};

export const getTypeHue = (type?: string): string =>
  typeHues[(type || '').toLowerCase()] ?? '#787774';

// ---------------------------------------------------------------------------
// Environment
// ---------------------------------------------------------------------------

export const getEnvironmentHue = (env?: string): string => {
  const e = (env || '').toLowerCase();
  if (e === 'prod' || e === 'production') return '#d93a3a';
  if (e === 'stage' || e === 'staging') return sticker.orange;
  if (e === 'dev' || e === 'development') return sticker.green;
  if (e === 'qa' || e === 'test') return '#8155b8';
  return '#787774';
};

// ---------------------------------------------------------------------------
// Notion-style tag rendering: faint tinted background + deep readable text.
// ---------------------------------------------------------------------------

export interface TagStyle {
  bg: string;
  text: string;
  border: string;
}

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return null;
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
};

export const alpha = (hex: string, a: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
};

/** Lighten a hue toward white by `amount` (0–1) — used for dark-mode text. */
export const lighten = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const l = (c: number) => Math.round(c + (255 - c) * amount);
  return `rgb(${l(rgb.r)}, ${l(rgb.g)}, ${l(rgb.b)})`;
};

export const tagStyle = (hue: string, mode: PaletteMode): TagStyle =>
  mode === 'dark'
    ? { bg: alpha(hue, 0.18), text: lighten(hue, 0.45), border: alpha(hue, 0.35) }
    : { bg: alpha(hue, 0.1), text: hue, border: alpha(hue, 0.28) };
